import * as React from 'react';

import { unwrapResult } from '@reduxjs/toolkit';
import { Form } from 'antd';
import { Notification } from 'farmhub-ui-core';
import { useHistory } from 'react-router-dom';

import { CreateInvitationBody, GoogleApi } from 'api';
import { useAppDispatch, useAppSelector } from 'app';
import {
  createAccount,
  findEntityPricePolicyForSelect,
  findEntityToConnect,
  findWithAuth,
  reloadAccounts,
  updateUserQuantity,
} from 'slice';
import { COLLABORATOR_TEXT, ENTITY_TYPE, GROUP_TYPE } from 'utils';
import {
  formatPhoneNumber,
  getSelectedCentralEntityID,
  isVietnamesePhoneNumber,
  removeEmptyElement,
} from 'utils/function';

import Content from './Content';
import EntityInfoStep from './EntityInfoStep';
import PartnerInfoStep from './PartnerInfoStep';
import PricePolicyStep from './PricePolicyStep';
import ProductStep from './ProductStep';

interface CollaboratorCreatePageContentProps {
  setVisibleModal?: (e: boolean) => void;
  setCurrentStep?: (e: number) => void;
}

export const CollaboratorInChainCreatePageContent = ({
  setVisibleModal,
  setCurrentStep,
}: CollaboratorCreatePageContentProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const [form] = Form.useForm();
  const accountList = useAppSelector(state => state.account.list);
  const entityID = useAppSelector(state => state.account.item?.id);
  const reloadAccountsFlag = useAppSelector(state => state.account.reloadFlag);
  const centralEntityID = useAppSelector(
    state => state.account?.item?.centralEntityID
  );
  const [hasErrorCode, setHasErrorCode] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [currentTab, setCurrentTab] = React.useState(0);
  const [bodySaved, setBodySaved] = React.useState<any>({});
  const [isValidEmail, setIsValidEmail] = React.useState(true);
  const [isValidPhoneNumber, setIsValidPhoneNumber] = React.useState(true);
  const [selectedPartner, setSelectedPartner] = React.useState<any>({});
  const [keySearchPartner, setKeySearchPartner] = React.useState('');
  const [isLoadingSearchPartner, setIsLoadingSearchPartner] =
    React.useState(true);
  const [currentPartnerGroupType, setCurrentPartnerGroupType] =
    React.useState<any>(1); // Role type: 0 'supplier', 1: 'customer'
  const [partnerList, setPartnerList] = React.useState<any[]>([]);

  const [totalProduct, setTotalProduct] = React.useState(0);
  const [selectedProducts, setSelectedProducts] = React.useState(
    [] as string[]
  );
  const [selectAllProducts, setSelectAllProducts] = React.useState(true);
  const [productQuery, setProductQuery] = React.useState<any>({
    page: 1,
    take: 10,
  });
  const [isPublicPricePolicy, setIsPublicPricePolicy] = React.useState(true);
  const [currentPartnerID, setCurrentPartnerID] = React.useState('');
  const [pricePolicies, setPricePolicies] = React.useState([]);
  const [selectedPricePolicyID, setSelectedPricePolicyID] = React.useState('');

  const handleChangeCurrentStep = (e: number) => {
    setCurrentTab(e);
  };

  const handleSubmitStep = async (current?: number) => {
    form.validateFields().then(async values => {
      if (currentTab === 0) {
        if (!isValidEmail || !isValidPhoneNumber) {
          return;
        }

        if (values.allCode) {
          delete values.allCode;
        }

        const location = form.getFieldsValue([
          'provinceCode',
          'districtCode',
          'wardCode',
          'latitude',
          'longitude',
          'address',
        ]);

        if (
          !location.provinceCode ||
          !location.districtCode ||
          !location.wardCode
        ) {
          setHasErrorCode(true);
          return;
        }

        if (
          location?.provinceCode &&
          location?.districtCode &&
          location?.wardCode &&
          !location?.latitude &&
          !location?.longitude
        ) {
          const result = await GoogleApi.getCenterPointWard({
            provinceCode: location?.provinceCode,
            districtCode: location?.districtCode,
            wardCode: location?.wardCode,
          });
          location.latitude = result.data.latitude;
          location.longitude = result.data.longitude;
        }

        const body = {
          ...values,
          ...location,
          type: ENTITY_TYPE.COLLABORATOR,
          displayName: values.fullName,
          phoneNumber: formatPhoneNumber(values.phoneNumber),
        };

        setBodySaved({
          ...bodySaved,
          ...body,
        });
      }

      if (currentTab === 1) {
        setBodySaved({
          ...bodySaved,
          invitationInfo: values,
        });
      }

      if (currentTab === 2) {
        if (!selectAllProducts && selectedProducts.length === 0) {
          Notification('warning', 'Bạn cần chọn ít nhất một sản phẩm');
          return;
        }
      }

      if (current) {
        if (current <= currentTab) {
          setCurrentTab(current);
        } else {
          setCurrentTab(currentTab + 1);
        }
      } else {
        setCurrentTab(currentTab + 1);
      }
    });
  };

  const handleFinalStep = async () => {
    const {
      entityID: bodyEntityID,
      partnerGroupID,
      introduction,
      shouldCreateDigitalMenu,
      partnerCode,
      type,
    } = bodySaved.invitationInfo;

    const invitationInfo: CreateInvitationBody = {
      entityID: bodyEntityID,
      partnerGroupID,
      introduction,
      inviterID: entityID,
      shouldCreateDigitalMenu,
      shouldSyncEntityCategory: true,
      shouldCreateNewProduct: true,
      partnerCode,
      groupType:
        type === 'CUSTOMER' ? GROUP_TYPE.SUPPLIER : GROUP_TYPE.CUSTOMER,
      entityPricePolicyID: selectedPricePolicyID || undefined,
    };

    if (selectAllProducts) {
      invitationInfo.isSelectAllProduct = true;
      invitationInfo.categoryID = productQuery.entityCategoryID;
      invitationInfo.search = productQuery.name;
    } else {
      invitationInfo.supplierProductIDs = selectedProducts;
    }
    removeEmptyElement(invitationInfo);

    const body = {
      ...bodySaved,
      invitationInfo,
    };
    removeEmptyElement(body);

    try {
      if (
        invitationInfo.supplierProductIDs &&
        invitationInfo.supplierProductIDs.length === 0
      ) {
        Notification('warning', 'Bạn chưa chọn sản phẩm liên kết');
        return;
      }
      setIsSubmitting(true);
      const actionResult: any = await dispatch(createAccount(body));
      unwrapResult(actionResult);
      setIsSubmitting(false);
      dispatch(
        updateUserQuantity({ count: 1, type: 'totalCollaboratorAccount' })
      );
      dispatch(reloadAccounts());
      setTimeout(() => {
        !setVisibleModal && history.push(`/account/list`);
        Notification(
          'success',
          `Tạo ${COLLABORATOR_TEXT.LOWERCASE} thành công`
        );
      }, 1000);
    } catch (error: any) {
      setIsSubmitting(false);
      Notification(
        'error',
        `Tạo ${COLLABORATOR_TEXT.LOWERCASE} thất bại`,
        error
      );
    }

    setIsSubmitting(false);
    setCurrentStep && setCurrentStep(0);
  };

  const handleNextStep = () => {
    switch (currentTab) {
      case 0:
      case 1:
        return handleSubmitStep();
      case 2:
        return currentPartnerGroupType === GROUP_TYPE.CUSTOMER ||
          !isPublicPricePolicy
          ? handleFinalStep()
          : handleSubmitStep();
      case 3:
        return handleFinalStep();
      default:
        return 0;
    }
  };

  React.useEffect(() => {
    const initPolicyPartner = async (): Promise<void> => {
      try {
        const result: any = await dispatch(
          findEntityPricePolicyForSelect({ entityID: currentPartnerID })
        ).unwrap();
        const { data } = result;
        const formatData = data.map(item => {
          return {
            key: item.id,
            ...item,
          };
        });
        if (formatData?.length > 0) {
          setSelectedPricePolicyID(formatData[0].id);
          setPricePolicies(formatData);
        } else {
          setIsPublicPricePolicy(false);
        }
      } catch (error: any) {
        Notification('error', 'Tìm chính sách đối tác thất bại', error);
      }
    };

    if (currentPartnerID && currentPartnerGroupType === GROUP_TYPE.SUPPLIER) {
      initPolicyPartner();
    }
  }, [currentPartnerGroupType, currentPartnerID, dispatch]);

  React.useEffect(() => {
    const initAccountList = async () => {
      try {
        await dispatch(findWithAuth({ body: {}, query: {} })).unwrap();
      } catch (error: any) {
        Notification('error', 'Tải dữ liệu thất bại', error);
      }
    };

    if (accountList.length == 0) {
      initAccountList();
    }
  }, [dispatch, reloadAccountsFlag]);

  React.useEffect(() => {
    const initPartnerList = async (search: string): Promise<void> => {
      setPartnerList([]);
      setIsLoadingSearchPartner(true);
      try {
        const name = isVietnamesePhoneNumber(search?.trim())
          ? formatPhoneNumber(search)
          : search?.replace(/\s+/g, ' ').trim();

        const selectedCentralEntityID = getSelectedCentralEntityID(
          accountList,
          centralEntityID
        );
        if (selectedCentralEntityID) {
          const { data } = await dispatch(
            findEntityToConnect({
              entityID: selectedCentralEntityID,
              name,
              take: 30,
              typeInChain: ENTITY_TYPE.COLLABORATOR,
            })
          ).unwrap();
          setPartnerList(data);
        }
      } catch (error: any) {
        Notification('error', 'Tìm đối tác thất bại', error);
      } finally {
        setIsLoadingSearchPartner(false);
      }
    };

    const delayTime = setTimeout(() => {
      initPartnerList(keySearchPartner);
    }, 800);
    return () => clearTimeout(delayTime);
  }, [
    keySearchPartner,
    dispatch,
    setPartnerList,
    entityID,
    accountList,
    centralEntityID,
  ]);

  const supplierStepList = [
    <EntityInfoStep
      form={form}
      hasErrorCode={hasErrorCode}
      isValidEmail={isValidEmail}
      isValidPhoneNumber={isValidPhoneNumber}
      setHasErrorCode={setHasErrorCode}
      setIsValidEmail={setIsValidEmail}
      setIsValidPhoneNumber={setIsValidPhoneNumber}
    />,
    <PartnerInfoStep
      currentPartnerGroupType={currentPartnerGroupType}
      currentPartnerID={currentPartnerID}
      form={form}
      isLoadingSearchPartner={isLoadingSearchPartner}
      partnerList={partnerList}
      selectedPartner={selectedPartner}
      setCurrentPartnerGroupType={setCurrentPartnerGroupType}
      setCurrentPartnerID={setCurrentPartnerID}
      setKeySearchPartner={setKeySearchPartner}
      setPartnerList={setPartnerList}
      setSelectedPartner={setSelectedPartner}
      setSelectedProducts={setSelectedProducts}
    />,
    <ProductStep
      form={form}
      query={productQuery}
      selectAllProducts={selectAllProducts}
      selectedProducts={selectedProducts}
      setQuery={setProductQuery}
      setSelectAllProducts={setSelectAllProducts}
      setSelectedProducts={setSelectedProducts}
      setTotalProduct={setTotalProduct}
      totalProduct={totalProduct}
    />,
  ];

  const customerStepList = [
    <EntityInfoStep
      form={form}
      hasErrorCode={hasErrorCode}
      isValidEmail={isValidEmail}
      isValidPhoneNumber={isValidPhoneNumber}
      setHasErrorCode={setHasErrorCode}
      setIsValidEmail={setIsValidEmail}
      setIsValidPhoneNumber={setIsValidPhoneNumber}
    />,
    <PartnerInfoStep
      currentPartnerGroupType={currentPartnerGroupType}
      currentPartnerID={currentPartnerID}
      form={form}
      isLoadingSearchPartner={isLoadingSearchPartner}
      partnerList={partnerList}
      selectedPartner={selectedPartner}
      setCurrentPartnerGroupType={setCurrentPartnerGroupType}
      setCurrentPartnerID={setCurrentPartnerID}
      setKeySearchPartner={setKeySearchPartner}
      setPartnerList={setPartnerList}
      setSelectedPartner={setSelectedPartner}
      setSelectedProducts={setSelectedProducts}
    />,
    <ProductStep
      form={form}
      query={productQuery}
      selectAllProducts={selectAllProducts}
      selectedProducts={selectedProducts}
      setQuery={setProductQuery}
      setSelectAllProducts={setSelectAllProducts}
      setSelectedProducts={setSelectedProducts}
      setTotalProduct={setTotalProduct}
      totalProduct={totalProduct}
    />,
    ...(isPublicPricePolicy
      ? [
          <PricePolicyStep
            pricePolicies={pricePolicies}
            selectedPricePolicyID={selectedPricePolicyID}
            setSelectedPricePolicyID={setSelectedPricePolicyID}
          />,
        ]
      : []),
  ];

  return (
    <Content
      ListStep={
        currentPartnerGroupType === GROUP_TYPE.CUSTOMER
          ? supplierStepList
          : customerStepList
      }
      currentTab={currentTab}
      handleChangeCurrentStep={handleChangeCurrentStep}
      handleNextStep={handleNextStep}
      handleSubmitStep={handleSubmitStep}
      isSubmitting={isSubmitting}
    />
  );
};
